// useTheme — fetches the tenant theme from /api/v1/company/theme and
// injects every column as a CSS custom property under :root.
//
// CLIENT-DECISION-002: theme is database-driven; CSS modules consume the
// variables and never hard-code hex values.
//
// Behaviour:
//   - Pre-login (status !== 'authenticated'): no fetch. The pre-login pages
//     use the defaults baked into global.css (which match COLOUR_SCHEME.md).
//   - Post-login: fetch once, inject into a <style id="tricivo-theme"> tag,
//     cache for 60 minutes.
//   - On fetch failure: keep the defaults from global.css. Never show a
//     broken UI.

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { endpoints } from '../api/endpoints';
import { useAuthStore } from '../store/authStore';
import { DEFAULT_THEME } from '../theme/defaults';
import type { CompanyTheme } from '../types/api';

const STYLE_TAG_ID = 'tricivo-theme';

function isColorKey(key: string): boolean {
  return key.startsWith('color_');
}

function buildCssText(theme: CompanyTheme): string {
  const lines: string[] = [':root {'];
  for (const [key, value] of Object.entries(theme)) {
    if (!isColorKey(key)) continue;
    if (typeof value !== 'string' || value.length === 0) continue;
    const cssVar = `--${key.replace(/_/g, '-')}`;
    lines.push(`  ${cssVar}: ${value};`);
  }
  lines.push('}');
  return lines.join('\n');
}

function injectTheme(theme: CompanyTheme): void {
  if (typeof document === 'undefined') return;
  let tag = document.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null;
  if (!tag) {
    tag = document.createElement('style');
    tag.id = STYLE_TAG_ID;
    document.head.appendChild(tag);
  }
  tag.textContent = buildCssText(theme);
}

export function useTheme() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const query = useQuery<CompanyTheme>({
    queryKey: ['company', 'theme'],
    queryFn: async () => {
      const res = await apiClient.get<CompanyTheme>(endpoints.company.theme);
      return res.data;
    },
    enabled: isAuthenticated,
    staleTime: 60 * 60 * 1000, // 60 minutes
    gcTime: 60 * 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    if (query.data) {
      injectTheme(query.data);
    } else if (query.isError) {
      // Fall back to defaults from COLOUR_SCHEME.md so the UI is still usable.
      injectTheme(DEFAULT_THEME);
    }
  }, [query.data, query.isError]);

  return query;
}
